import { useState } from 'react';
import { CardGrid } from '../../components/CardGrid';
import { ContentArea } from '../../components/ContentArea';
import { Dropdown } from '../../components/Dropdown';
import { EmptyMessage } from '../../components/EmptyMessage';
import { FilterPill, FilterPillGroup } from '../../components/FilterPill';
import { Section, Title, TitleRow } from '../../components/Section';
import { SkeletonCard } from '../../components/SkeletonCard';
import { SpaDayCard } from '../../composites/SpaDayCard';
import type { SpaListTemplateProps } from './SpaListTemplate.types';

const ALL_DURATIONS = '__all__';

export function SpaListTemplate({
  items,
  onSelectItem,
  isLoading,
  durationOptions,
  categoryOptions,
  selectedCategory,
  onCategoryChange,
  titleLabel = 'Spa days',
  fromPriceLabel = 'from',
  allDurationsLabel = 'All Duration',
  categoryLabel = 'Category',
  soldOutLabel = 'Sold out',
  noResultsLabel = 'No spa days available.',
  noImageLabel = 'No image',
  formatGuests,
}: SpaListTemplateProps) {
  const [selectedDuration, setSelectedDuration] = useState<string>(ALL_DURATIONS);

  const filteredItems =
    selectedDuration === ALL_DURATIONS
      ? items
      : items.filter((item) => item.durationValue === selectedDuration);

  const durationFilters = durationOptions && durationOptions.length > 0 && (
    <FilterPillGroup>
      <FilterPill
        type="button"
        $active={selectedDuration === ALL_DURATIONS}
        onClick={() => setSelectedDuration(ALL_DURATIONS)}
      >
        {allDurationsLabel}
      </FilterPill>
      {durationOptions.map((option) => (
        <FilterPill
          key={option.value}
          type="button"
          $active={selectedDuration === option.value}
          onClick={() => setSelectedDuration(option.value)}
        >
          {option.label}
        </FilterPill>
      ))}
    </FilterPillGroup>
  );

  const categoryDropdown = categoryOptions && categoryOptions.length > 0 && onCategoryChange && (
    <Dropdown
      options={categoryOptions}
      value={selectedCategory}
      placeholder={categoryLabel}
      onChange={onCategoryChange}
      variant="pill"
      aria-label={categoryLabel}
    />
  );

  if (isLoading && items.length === 0) {
    return (
      <Section>
        <TitleRow>
          <Title>{titleLabel}</Title>
          {categoryDropdown}
        </TitleRow>
        <CardGrid>
          {Array.from({ length: 6 }).map((_, index) => (
            <SkeletonCard key={index} variant="grid" />
          ))}
        </CardGrid>
      </Section>
    );
  }

  if (!isLoading && items.length === 0) {
    return (
      <Section>
        <TitleRow>
          <Title>{titleLabel}</Title>
          {categoryDropdown}
        </TitleRow>
        <EmptyMessage>{noResultsLabel}</EmptyMessage>
      </Section>
    );
  }

  return (
    <Section>
      <TitleRow>
        <Title>{titleLabel}</Title>
        {durationFilters}
        {categoryDropdown}
      </TitleRow>

      <ContentArea>
        {filteredItems.length === 0 ? (
          <EmptyMessage>{noResultsLabel}</EmptyMessage>
        ) : (
          <CardGrid>
            {filteredItems.map((item) => (
              <SpaDayCard
                key={item.id}
                id={item.id}
                name={item.name}
                imageUrl={item.imageUrl}
                price={item.price}
                discount={item.discount}
                duration={item.duration}
                numberOfGuests={item.numberOfGuests}
                unavailable={item.unavailable}
                fromPriceLabel={fromPriceLabel}
                soldOutLabel={soldOutLabel}
                noImageLabel={noImageLabel}
                formatGuests={formatGuests}
                onSelect={onSelectItem}
              />
            ))}
          </CardGrid>
        )}
      </ContentArea>
    </Section>
  );
}
